import { gql } from "@apollo/client";

export const CREATE_INVOICE = gql`
  mutation createSaleDocument($input: SaleDocumentCreateInput!) {
    createSaleDocument(input: $input) {
      errors {
        field
        messages
      }
      success
      saleDocument {
        id
        group {
          id
        }
      }
    }
  }
`;

export const CREATE_PERIODIC = gql`
  mutation createPeriodicSaleDocument($input: PeriodicSaleDocumentCreateInput!) {
    createPeriodicSaleDocument(input: $input) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const UPDATE_PERIODIC = gql`
  mutation updatePeriodicSaleDocument($input: PeriodicSaleDocumentUpdateInput!) {
    updatePeriodicSaleDocument(input: $input) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const CREATE_INVOICE_LINE = gql`
  mutation createSaleDocumentLine($input: SaleDocumentLineCreateInput!) {
    createSaleDocumentLine(input: $input) {
      errors {
        field
        messages
      }
      success
      saleDocumentLine {
        id
        order
      }
    }
  }
`;

export const UPDATE_INVOICE_LINE = gql`
  mutation updateSaleDocumentLine($input: SaleDocumentLineUpdateInput!) {
    updateSaleDocumentLine(input: $input) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const GET_DOCUMENTS_ENUM_OPTIONS = gql`
  query {
    vatType: listVatTypes {
      label
      value
    }
    invoiceType: listSaleDocumentTypes {
      label
      value
    }
  }
`;

export const UPDATE_SALE_DOCUMENT_LINES_ORDER = gql`
  mutation updateSaleDocumentLinesOrder($input: UpdateLinesOrderInput!) {
    updateSaleDocumentLinesOrder(input: $input) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

const SALE_DOCUMENT_LIST_FIELDS = gql`
  fragment SaleDocumentListFields on SaleDocumentType {
    id
    name
    date
    reference
    exported
    validUntil
    balance
    netWithDiscountPrice
    totalWithDiscountPrice
    totalNetWithAllDiscounts
    status {
      label
      value
    }
    state {
      label
      value
    }
    stage {
      label
      value
    }
    type {
      label
      value
    }
    priceType {
      value
    }
    emailSent {
      label
      value
    }
    client {
      id
      name
      email
    }
    group {
      id
      currency {
        code
        decimalPlaces
      }
    }
  }
`;

export const LIST_SALE_DOCUMENTS = gql`
  ${SALE_DOCUMENT_LIST_FIELDS}
  query listSaleDocuments(
    $filter: SaleDocumentFilter
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listSaleDocuments(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        ...SaleDocumentListFields
      }
    }
  }
`;

export const LIST_SALE_DOCUMENTS_SELECT_ALL = gql`
  query listSaleDocuments(
    $filter: SaleDocumentFilter
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listSaleDocuments(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        type {
          label
          value
        }
        status {
          label
          value
        }
      }
    }
  }
`;

export const LIST_SALE_DOCUMENT_SELECT = gql`
  query listSaleDocumentSelect(
    $filter: SaleDocumentFilter
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listSaleDocuments(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        reference
        name
        date
        debt
      }
    }
  }
`;

export const LIST_RECEIPTS = gql`
  query listReceipts($filter: ReceiptsFilter, $offset: Int, $limit: Int, $orderBy: [String!]) {
    listReceipts(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        client {
          id
          name
          email
        }
        totalDefaultCurrency
        date
        reference
        total
        decimalPlaces
        currencyCode
      }
    }
  }
`;

export const GET_RECEIPT_URL_PDF = gql`
  query getReceiptUrl($id: ID!) {
    getReceiptUrl(id: $id)
  }
`;

export const LIST_EMAIL_STATUS = gql`
  query listEmailStatus($filter: ListEmailStatusFilter) {
    listEmailStatus(filter: $filter) {
      id
      email
      createdAt
      status {
        label
        value
      }
    }
  }
`;

export const LIST_INVOICES_ACTIONS = gql`
  query listSaleDocumentsActions($idIn: [ID!]!) {
    listSaleDocumentsActions(idIn: $idIn) {
      canProduce
    }
  }
`;

export const LIST_PERIODICALS = gql`
  query listPeriodicSaleDocuments(
    $filter: PeriodicSaleDocumentFilter
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listPeriodicSaleDocuments(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        days
        first
        generationType
        name
        namePreview
        originalSaleDocument {
          id
          client {
            id
            name
            code
          }
        }
        period
        total
        isActive
      }
    }
  }
`;

const ADDRESS_FIELDS = gql`
  fragment AddressFields on ClientAddress {
    id
    name
    address1
    address2
    addressBtq
    addressNr
    city
    postalCode
  }
`;

const SALE_DOCUMENT_LINE_FIELDS = gql`
  fragment SaleDocumentLineFields on SaleDocumentLineType {
    id
    order
    type {
      label
      value
    }
    product {
      id
      nameTranslations {
        language
        value
      }
      descriptionTranslations {
        language
        value
      }
    }
    description
    title
    quantity
    unitPrice
    discount
    discountLabel
    subsidiary {
      id
      name
    }
    vat {
      id
      rate
    }
    comment {
      id
      name
    }
    unitMeasure {
      id
      name
      slug
    }
  }
`;

export const GET_SALE_DOCUMENT_LINES = gql`
  ${SALE_DOCUMENT_LINE_FIELDS}
  query getSaleDocumentLines($id: ID!) {
    getSaleDocument(id: $id) {
      id
      group {
        id
        currency {
          code
          decimalPlaces
        }
      }
      lines {
        ...SaleDocumentLineFields
      }
    }
  }
`;

export const GET_SALE_DOCUMENT = gql`
  ${ADDRESS_FIELDS}
  ${SALE_DOCUMENT_LINE_FIELDS}
  query getSaleDocument($id: ID!) {
    getSaleDocument(id: $id) {
      id
      date
      name
      description
      templateLanguage
      reference
      debt
      comment
      discount
      discountLabel
      latePaymentPenaltyComment
      fixedRecoveryFeeComment
      discountComment
      subsidiaryId
      subsidiary {
        id
        name
      }
      validUntil
      paymentDeadlineDate
      stampSpace
      totalPrice
      periodic
      client {
        id
        code
        name
        email
      }
      clientConfig {
        note
      }
      group {
        id
        currency {
          id
          code
          decimalPlaces
        }
        receipts {
          id
          reference
        }
        saleDocuments {
          id
          reference
          totalValue
          status {
            label
            value
          }
          type {
            label
            value
          }
        }
      }
      billingAddress {
        ...AddressFields
      }
      deliveryAddress {
        ...AddressFields
      }
      type {
        label
        value
      }
      status {
        label
        value
      }
      priceType {
        label
        value
      }
      vatType {
        label
        value
      }
      paymentMethod {
        id
        name
      }
      paymentDeadline {
        id
        name
        beginningDay
        payDay
        numberOfDays
      }
      sepa {
        id
      }
      originalDocument {
        totalValue
        paymentInAdvancePercentage
        type {
          label
          value
        }
        status {
          label
          value
        }
        id
      }
      billingAddressName
      billingAddress1
      billingAddress2
      billingAddressBtq
      billingAddressNr
      billingCity
      billingPostalCode
      billingCountry
      billingAddressCountry {
        code
        name
      }
      deliveryAddressName
      deliveryAddress1
      deliveryAddress2
      deliveryAddressBtq
      deliveryAddressNr
      deliveryCity
      deliveryPostalCode
      deliveryCountry
      deliveryAddressCountry {
        code
        name
      }
      lines {
        ...SaleDocumentLineFields
      }
    }
  }
`;

export const GET_PERIODIC_SALE_DOCUMENT = gql`
  query getPeriodicSaleDocument($id: ID!) {
    getPeriodicSaleDocument(id: $id) {
      id
      days
      first
      generationType
      name
      namePreview
      originalSaleDocument {
        id
      }
      period
      total
    }
  }
`;

export const GET_SALE_DOCUMENT_PDF = gql`
  query getSaleDocumentPdf($id: ID!) {
    getSaleDocumentPdf(id: $id)
    getSaleDocument(id: $id) {
      id
      publicId
      type {
        label
        value
      }
      name
      reference
    }
  }
`;

export const GET_INVOICE_XLXSX = gql`
  query getSaleDocumentsXlsx($filter: SaleDocumentFilter!) {
    getSaleDocumentsXlsx(filter: $filter)
  }
`;

export const DOWNLOAD_INVOICES = gql`
  query downloadSaleDocuments($id_In: [ID!]!, $joinPdfs: Boolean!) {
    downloadSaleDocuments(id_In: $id_In, joinPdfs: $joinPdfs)
  }
`;

export const UPDATE_INVOICE = gql`
  mutation updateSaleDocument($input: SaleDocumentUpdateInput!) {
    updateSaleDocument(input: $input) {
      success
      errors {
        field
        messages
      }
      saleDocument {
        lines {
          id
          discount
          discountLabel
          subsidiary {
            id
            name
          }
          vat {
            id
            rate
          }
          unitPrice
          type {
            label
            value
          }
          quantity
          product {
            id
            name
          }
          description
        }
        id
        vatType {
          label
          value
        }
        date
      }
    }
  }
`;

export const PRODUCE_INVOICE = gql`
  mutation produceSaleDocument($id: ID!) {
    produceSaleDocument(id: $id) {
      success
      errors {
        field
        messages
      }
      saleDocument {
        id
        status {
          label
          value
        }
      }
    }
  }
`;

export const VALIDATE_INVOICE = gql`
  mutation validateSaleDocument($id: ID!) {
    validateSaleDocument(id: $id) {
      success
      errors {
        field
        messages
      }
      saleDocument {
        id
        status {
          label
          value
        }
      }
    }
  }
`;

export const GENERATE_INVOICE_TYPE = gql`
  mutation generateSaleDocumentType($input: SaleDocumentGenerateInput!) {
    generateSaleDocumentType(input: $input) {
      success
      errors {
        field
        messages
      }
      saleDocument {
        id
      }
    }
  }
`;

export const SEND_DOCUMENT_EMAIL = gql`
  mutation sendDocumentEmail($input: SendDocumentEmailInput!) {
    sendDocumentEmail(input: $input) {
      success
      errors {
        field
        messages
      }
    }
  }
`;

export const SEND_MULTIPLE_DOCUMENT_EMAIL = gql`
  mutation sendMultipleDocumentsEmails($input: SendMultipleDocumentsEmailsInput!) {
    sendMultipleDocumentsEmails(input: $input) {
      success
      errors {
        field
        messages
      }
    }
  }
`;

export const DELETE_INVOICE_LINE = gql`
  mutation deleteSaleDocumentLine($id: ID!) {
    deleteSaleDocumentLine(id: $id) {
      success
      errors {
        field
        messages
      }
    }
  }
`;

export const ACCEPT_INVOICE = gql`
  mutation acceptSaleDocument($id: ID!) {
    acceptSaleDocument(id: $id) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const ANNULL_INVOICE = gql`
  mutation annulSaleDocument($id: ID!) {
    annulSaleDocument(id: $id) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const REFUSE_INVOICE = gql`
  mutation refuseSaleDocument($id: ID!) {
    refuseSaleDocument(id: $id) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const DUPLICATE_INVOICE = gql`
  mutation duplicateSaleDocument($id: ID!) {
    duplicateSaleDocument(id: $id) {
      success
      errors {
        messages
        field
      }
      saleDocument {
        id
        type {
          label
          value
        }
      }
    }
  }
`;

export const DELETE_INVOICE = gql`
  mutation deleteSaleDocument($id: ID!) {
    deleteSaleDocument(id: $id) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const DELETE_INVOICE_LIST = gql`
  mutation deleteSaleDocumentList($id_In: [ID!]!) {
    deleteSaleDocumentList(id_In: $id_In) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const DUPLICATE_INVOICE_LIST = gql`
  mutation duplicateSaleDocumentList($id_In: [ID!]!) {
    duplicateSaleDocumentList(id_In: $id_In) {
      saleDocument
      original
    }
  }
`;

export const LIST_SALE_DOCUMENT_STATUS_HISTORY = gql`
  query listSaleDocumentStatusHistory(
    $filter: SaleDocumentStatusHistoryFilter!
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listSaleDocumentStatusHistory(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        createdAt
        status {
          label
          value
        }
      }
    }
  }
`;

export const LIST_PERIODIC_TYPES = gql`
  query periodicTypes {
    listPeriodicSaleDocumentPeriods {
      label
      value
    }
    listPeriodicSaleDocumentGenerationType {
      label
      value
    }
  }
`;

export const ACTIVE_PERIODIC = gql`
  mutation activePeriodicSaleDocument($id: ID!) {
    activePeriodicSaleDocument(id: $id) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const STOP_PERIODIC = gql`
  mutation stopPeriodicSaleDocument($id: ID!) {
    stopPeriodicSaleDocument(id: $id) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const DELETE_PERIODIC_SALE_DOCUMENT = gql`
  mutation deletePeriodicSaleDocument($id: ID!) {
    deletePeriodicSaleDocument(id: $id) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const PRODUCE_INVOICE_LIST = gql`
  mutation produceSaleDocumentList($idIn: [ID!]!) {
    produceSaleDocumentList(idIn: $idIn) {
      errors {
        field
        messages
        saleDocumentIds
      }
      success
      saleDocuments {
        success
        errors {
          field
          messages
        }
        saleDocument {
          id
          date
          name
          status {
            label
            value
          }
          client {
            name
          }
          totalPrice
          totalWithDiscountPrice
        }
      }
    }
  }
`;
export const ACCEPT_SALE_DOCUMENT_LIST = gql`
  mutation acceptSaleDocumentList($idIn: [ID!]!) {
    acceptSaleDocumentList(idIn: $idIn) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const VALIDATE_SALE_DOCUMENT_DATE = gql`
  mutation validateSaleDocumentDate($input: ValidateSaleDocumentDateInput!) {
    validateSaleDocumentDate(input: $input) {
      lastDate
      totalDocuments
      invalidDocuments {
        saleDocument {
          id
          date
          name
          status {
            label
            value
          }
          client {
            name
          }
          totalPrice
        }
        errors {
          field
          messages
        }
      }
    }
  }
`;

export const UPDATE_SALE_DOCUMENT_DATE = gql`
  mutation updateSaleDocumentDate($input: UpdateSaleDocumentDateInput!) {
    updateSaleDocumentDate(input: $input) {
      success
      errors {
        messages
        field
      }
    }
  }
`;

export const VALIDATE_INVOICE_LIST = gql`
  mutation validateSaleDocumentList($id_In: [ID!]!) {
    validateSaleDocumentList(id_In: $id_In) {
      saleDocuments {
        success
        errors {
          field
          messages
        }
        saleDocument {
          id
          date
          client {
            name
          }
          totalWithDiscountPrice
        }
      }
    }
  }
`;

export const CLIENT_HAS_RETURN_DOCUMENTS_UNPAID = gql`
  query clientHasReturnDocumentsUnpaid($companyId: ID!, $clientId: ID!) {
    clientHasReturnDocumentsUnpaid(companyId: $companyId, clientId: $clientId)
  }
`;

export const LIST_SALE_DOCUMENTS_LATEST_MODIFIED = gql`
  query listSaleDocuments(
    $filter: SaleDocumentFilter
    $offset: Int
    $limit: Int
    $orderBy: [String!]
  ) {
    listSaleDocuments(filter: $filter) {
      totalCount
      results(offset: $offset, limit: $limit, orderBy: $orderBy) {
        id
        date
        netPrice
        status {
          label
          value
        }
        type {
          label
        }
        client {
          name
        }
        group {
          currency {
            id
            code
            decimalPlaces
          }
        }
      }
    }
  }
`;

export const ACCEPT_SALE_DOCUMENT = gql`
  mutation acceptSaleDocument($id: ID!, $token: String!, $signedFile: Upload) {
    acceptSaleDocument(id: $id, token: $token, signedFile: $signedFile) {
      errors {
        field
        messages
      }
      success
    }
  }
`;

export const REFUSE_SALE_DOCUMENT = gql`
  mutation refuseSaleDocument($id: ID!, $token: String!, $signedFile: Upload) {
    refuseSaleDocument(id: $id, token: $token, signedFile: $signedFile) {
      errors {
        field
        messages
      }
      success
    }
  }
`;
export const GET_SALE_DOCUMENT_BUDGET = gql`
  query getSaleDocumentBudget($uuid: ID!) {
    getSaleDocumentBudget(uuid: $uuid) {
      saleDocumentId
      pdfUrl
    }
  }
`;

export const GET_LAST_VALIDATED_DOCUMENT_DATE = gql`
  query getLastValidatedDocumentDate($companyId: ID!) {
    getLastValidatedDocumentDate(filter: { companyId: $companyId })
  }
`;

export const GENERATE_SALE_DOCUMENT_TYPE_ADVANCE_PAYMENT = gql`
  mutation generateSaleDocumentTypeAdvancePayment($input: SaleDocumentAdvancePaymentInput!) {
    generateSaleDocumentTypeAdvancePayment(input: $input) {
      errors {
        field
        messages
      }
      success
      saleDocument {
        id
      }
    }
  }
`;
